import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

export interface PWAContextValue {
  // 网络状态
  isOnline: boolean
  // Service Worker 状态
  needRefresh: boolean
  offlineReady: boolean
  // 安装状态
  canInstall: boolean
  isInstalled: boolean
  // 操作方法
  updateServiceWorker: () => Promise<void>
  installApp: () => Promise<void>
  dismissInstallPrompt: () => void
}

const PWAContext = createContext<PWAContextValue | null>(null)

interface PWAProviderProps {
  children: ReactNode
}

export function PWAProvider({ children }: PWAProviderProps) {
  // 网络状态
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  // 安装状态
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)

  // Service Worker 注册
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log('SW Registered:', r)
    },
    onRegisterError(error) {
      console.log('SW registration error', error)
    },
  })

  // 监听网络状态变化
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // 监听安装提示事件
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as BeforeInstallPromptEvent)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setInstallPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    // 检查是否已安装
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  // 安装应用
  const installApp = useCallback(async () => {
    if (!installPrompt) {return}

    const result = await installPrompt.prompt()
    console.log('Install prompt result:', result)

    if (result.outcome === 'accepted') {
      setIsInstalled(true)
    }
    setInstallPrompt(null)
  }, [installPrompt])

  // 关闭安装提示
  const dismissInstallPrompt = useCallback(() => {
    setInstallPrompt(null)
  }, [])

  // 更新 Service Worker
  const handleUpdateServiceWorker = useCallback(async () => {
    await updateServiceWorker(true)
    setNeedRefresh(false)
    setOfflineReady(false)
  }, [updateServiceWorker, setNeedRefresh, setOfflineReady])

  const value: PWAContextValue = {
    isOnline,
    needRefresh,
    offlineReady,
    canInstall: !!installPrompt,
    isInstalled,
    updateServiceWorker: handleUpdateServiceWorker,
    installApp,
    dismissInstallPrompt,
  }

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>
}

export function usePWA() {
  const context = useContext(PWAContext)
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider')
  }
  return context
}

// BeforeInstallPromptEvent 类型定义
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<{ outcome: 'accepted' | 'dismissed' }>
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent
  }
}
